import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, DollarSign, Activity, Clock, PieChart, BarChart3, Download } from 'lucide-react';

const AdvancedAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [error, setError] = useState(null);

  const fetchAnalytics = async (days) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/super-admin/analytics/advanced?days=${days}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setAnalytics(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Error al cargar analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics(period);
  }, [period]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'VES',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Cargando analytics...</span>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="text-center py-8 text-red-600">
        {error || 'No se pudieron cargar los analytics'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Selector de Período */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Período de Análisis</h3>
          <div className="flex gap-2">
            {[7, 15, 30, 60, 90].map(days => (
              <button
                key={days}
                onClick={() => setPeriod(days)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  period === days
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {days} días
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Métricas Clave */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Tasa de Conversión */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Tasa de Conversión</p>
              <p className="text-3xl font-bold text-green-600 mt-2">
                {formatPercent(analytics.orders.conversion_rate)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {analytics.orders.paid} de {analytics.orders.total} órdenes pagadas
              </p>
            </div>
            <TrendingUp className="w-12 h-12 text-green-500 opacity-20" />
          </div>
        </div>

        {/* Valor Promedio */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Valor Promedio</p>
              <p className="text-2xl font-bold text-blue-600 mt-2">
                {formatCurrency(analytics.orders.avg_order_value)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Por transacción</p>
            </div>
            <DollarSign className="w-12 h-12 text-blue-500 opacity-20" />
          </div>
        </div>

        {/* Tiempo Promedio de Pago */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Tiempo Promedio</p>
              <p className="text-3xl font-bold text-purple-600 mt-2">
                {analytics.payment_time.avg_minutes.toFixed(0)}<span className="text-lg">min</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">Desde creación hasta pago</p>
            </div>
            <Clock className="w-12 h-12 text-purple-500 opacity-20" />
          </div>
        </div>

        {/* Tasa de Expiración */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Tasa de Expiración</p>
              <p className="text-3xl font-bold text-orange-600 mt-2">
                {formatPercent(analytics.orders.expiration_rate)}
              </p>
              <p className="text-xs text-gray-500 mt-1">{analytics.orders.expired} órdenes expiradas</p>
            </div>
            <TrendingDown className="w-12 h-12 text-orange-500 opacity-20" />
          </div>
        </div>
      </div>

      {/* Métodos de Pago - Distribución */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-semibold mb-4 flex items-center">
          <PieChart className="w-6 h-6 mr-2 text-blue-600" />
          Distribución por Método de Pago
        </h3>
        <div className="space-y-4">
          {analytics.payment_methods.map((method, idx) => {
            const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500'];
            const color = colors[idx % colors.length];
            
            return (
              <div key={method.method} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium capitalize">
                    {method.method === 'p2p' && '📱 Pago Móvil P2P'}
                    {method.method === 'c2p' && '💳 Cobro a Persona'}
                    {method.method === 'bcv_transfer' && '🏦 Transferencia BCV'}
                    {method.method === 'immediate_debit' && '💰 Débito Inmediato'}
                    {!['p2p', 'c2p', 'bcv_transfer', 'immediate_debit'].includes(method.method) && method.method}
                  </span>
                  <div className="text-right">
                    <span className="font-bold">{formatCurrency(method.volume)}</span>
                    <span className="text-gray-500 ml-2">({method.transactions} txs)</span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`${color} h-3 rounded-full transition-all duration-500`}
                    style={{ width: `${method.percentage}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{formatPercent(method.percentage)} del volumen</span>
                  <span>Ticket promedio: {formatCurrency(method.avg_ticket)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top Merchants */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-semibold mb-4 flex items-center">
          <BarChart3 className="w-6 h-6 mr-2 text-blue-600" />
          Top 10 Merchants por Volumen
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Merchant</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Volumen</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Transacciones</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ticket Promedio</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Comisión</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {analytics.top_merchants.map((merchant, idx) => (
                <tr key={merchant.merchant_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {idx + 1}
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{merchant.company_name}</p>
                      <p className="text-xs text-gray-500">{merchant.email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-right text-blue-600">
                    {formatCurrency(merchant.volume)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">{merchant.transactions}</td>
                  <td className="px-4 py-3 text-sm text-right">{formatCurrency(merchant.avg_transaction)}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-right text-green-600">
                    {formatCurrency(merchant.commission)}
                  </td>
                </tr>
              ))}
              {analytics.top_merchants.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    No hay datos en este período
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tendencias Diarias */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-semibold mb-4 flex items-center">
          <Activity className="w-6 h-6 mr-2 text-blue-600" />
          Tendencia Diaria de Transacciones
        </h3>
        
        {analytics.daily_trends.length > 0 ? (
          <div className="space-y-3">
            {analytics.daily_trends.slice(-14).map((day) => {
              const maxVolume = Math.max(...analytics.daily_trends.map(d => d.volume));
              const barWidth = (day.volume / maxVolume * 100);
              
              return (
                <div key={day.date} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-700">{new Date(day.date).toLocaleDateString('es-VE')}</span>
                    <div className="text-right">
                      <span className="font-bold text-blue-600">{formatCurrency(day.volume)}</span>
                      <span className="text-gray-500 ml-2">({day.transactions} txs)</span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${barWidth}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-center text-gray-500 py-8">No hay datos de tendencias</p>
        )}
      </div>

      {/* Análisis de Dispersiones */}
      {analytics.dispersions.total > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4">Estado de Dispersiones</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600">Total Dispersiones</p>
              <p className="text-3xl font-bold text-blue-600 mt-2">{analytics.dispersions.total}</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600">Exitosas</p>
              <p className="text-3xl font-bold text-green-600 mt-2">{analytics.dispersions.successful}</p>
              <p className="text-xs text-gray-500 mt-1">{formatPercent(analytics.dispersions.success_rate)}</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-sm text-gray-600">Fallidas</p>
              <p className="text-3xl font-bold text-red-600 mt-2">{analytics.dispersions.failed}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedAnalytics;
